import {
  AlertCircle,
  Bot,
  ClipboardCheck,
  CreditCard,
  Leaf,
  List,
  Map as IconeMapa,
  Palette,
  Percent,
  Settings,
  Upload,
  Users,
  Wallet,
} from "lucide-react";
import { podeFazer, type Capacidade } from "@shared/permissoes";
import { orgVeAssinatura, type StatusOrg } from "@shared/org-status";

/**
 * A seção ADMINISTRAÇÃO do menu lateral, como lista pura.
 *
 * Mora fora do `app-sidebar.tsx` para poder ser testada sem montar a sidebar
 * (contexto, rota, queries): é aqui que se decide quem vê o quê, e é isso que o
 * teste vigia (`menu-administracao.test.ts`).
 */

export type ItemAdmin = { path: string; label: string; icon: typeof Users; cap: Capacidade };

export interface ContextoMenuAdmin {
  /** Os módulos EFETIVOS do usuário (`useAuth().modulesEnabled`). */
  modulesEnabled: string[];
  orgTipo: "PRODUTOR" | "COMPRADOR";
  /** O papel da sessão (em modo suporte, LEITURA). */
  papel: string | null;
  /** `user.orgStatus` (shared/org-status.ts). Desconhecido = como antes. */
  orgStatus?: StatusOrg | null;
}

export function itensDeAdministracao({ modulesEnabled, orgTipo, papel, orgStatus }: ContextoMenuAdmin): ItemAdmin[] {
  // Administração é transversal: a lista tem que ser a MESMA nos três módulos.
  // Funcionários e Atividades apareciam só quando o módulo atual era Equipe —
  // então bastava estar no Manejo pra eles sumirem, mesmo a org tendo Equipe.
  // O critério certo é a org TER o módulo, não o usuário estar nele agora.
  // Cada item de administração declara a CAPACIDADE que a tela exige (PR 2):
  // a seção é filtrada por `podeFazer`, não por `isAdmin` — o GESTOR vê
  // Funcionários e Remuneração; só o ADMIN vê Usuários, Configurações e Assinatura.
  const equipeAdminItems: ItemAdmin[] = modulesEnabled.includes("equipe")
    ? [
        { path: "/equipe/funcionarios", label: "Funcionários", icon: Users,  cap: "equipe.funcionarios.gerir" },
        { path: "/equipe/atividades",   label: "Atividades",   icon: List,   cap: "equipe.funcionarios.gerir" },
        { path: "/equipe/remuneracao",  label: "Remuneração",  icon: Wallet, cap: "equipe.remuneracao.configurar" },
      ]
    : [];

  // Auditoria mora dentro do Manejo — Auditorias e o Dashboard (consulta
  // frequente, não configuração) ficam no nav do módulo; só os dois cadastros
  // (Critérios auditoria, Faixas de nota) são transversais como o resto desta lista.
  const manejoAdminItems: ItemAdmin[] = modulesEnabled.includes("manejo")
    ? [
        { path: "/manejo/auditoria/criterios", label: "Critérios auditoria", icon: ClipboardCheck, cap: "auditoria.configurar" },
        { path: "/manejo/auditoria/faixas",    label: "Faixas de nota",      icon: Percent,        cap: "auditoria.configurar" },
      ]
    : [];

  // Cadastros que moram sob /cacho por herança histórica, mais Configurações
  // (parâmetros do emissor, compradores, produtos) e Assinatura. Sempre foram do
  // Cacho; até o Mercado C2 isso não precisava ser dito porque TODA org tinha o
  // Cacho. A org de COMPRADOR não tem — e sem este gate ela veria "Setores",
  // "Cores de Fitas" e "Importar Planilha" no menu, todas com 403 do outro lado.
  //
  // "Assinatura" some para org em CORTESIA (lançamento gratuito, nov/2026 — ver
  // `orgVeAssinatura`). A ROTA continua viva: quem tiver o link abre a tela.
  const cachoAdminItems: ItemAdmin[] = modulesEnabled.includes("cacho")
    ? [
        { path: "/cacho/variedades",      label: "Culturas e Variedades", icon: Leaf,    cap: "cacho.cadastros" },
        { path: "/cacho/setores",         label: "Setores",           icon: IconeMapa,   cap: "cacho.cadastros" },
        { path: "/cacho/cores",           label: "Cores de Fitas",    icon: Palette,     cap: "cacho.cadastros" },
        { path: "/cacho/motivos-perdas",  label: "Motivos de Perda",  icon: AlertCircle, cap: "cacho.cadastros" },
        { path: "/cacho/importar",        label: "Importar Planilha", icon: Upload,      cap: "cacho.cadastros" },
        { path: "/admin/configuracoes",   label: "Configurações",     icon: Settings,    cap: "org.configurar" },
        ...(orgVeAssinatura(orgStatus)
          ? [{ path: "/admin/assinatura", label: "Assinatura",        icon: CreditCard,  cap: "org.assinatura" } as ItemAdmin]
          : []),
      ]
    : [];

  // Agente é transversal ENTRE os módulos da fazenda: o chat atende o Manejo e o
  // boletim atravessa Cacho, Equipe e Manejo. Para quem não tem nenhum dos três
  // (o corretor) ele não teria sobre o que falar.
  const agenteItem: ItemAdmin[] = ["cacho", "equipe", "manejo"].some(m => modulesEnabled.includes(m))
    ? [{ path: "/agente", label: "Agente", icon: Bot, cap: "agente.usar" }]
    : [];

  // ⚠️ A ORG DE COMPRADOR NÃO TEM ADMINISTRAÇÃO (13/09/2026).
  //
  // Para ela a seção tinha UM item — "Usuários" —, e aquela tela é do Cacho: mostra
  // "papel" e "acesso às abas", eixos que não existem do lado do comprador, e o
  // "Novo usuário" cria conta COM SENHA. Isso é o oposto da regra que sustenta o
  // Mercado: a conta do comprador nasce do claim, pelo link que prova posse — nunca
  // de uma senha que alguém precisa transmitir por WhatsApp (ver
  // `shared/plataforma-organizacoes.ts`). Um cabeçalho "Administração" para segurar
  // um item que contradiz a regra é pior que nenhum.
  //
  // Quando o corretor precisar dar acesso a um sócio, o caminho é o mesmo do dono:
  // mandar o link de um documento, que anexa à organização existente (§B.3).
  if (orgTipo === "COMPRADOR") return [];
  return [
    ...agenteItem,
    ...equipeAdminItems,
    ...manejoAdminItems,
    { path: "/admin/usuarios", label: "Usuários", icon: Users, cap: "org.usuarios" } as ItemAdmin,
    ...cachoAdminItems,
  ].filter(item => podeFazer(papel, item.cap));
}
